import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { agenteIntegradorService } from '@/services/api';
import { AgenteIntegrador } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

const agenteIntegradorSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
});

type AgenteIntegradorFormData = Omit<AgenteIntegrador, 'id' | 'createdAt' | 'updatedAt'>;

export const NovoAgenteIntegradorPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateAgentesIntegradores } = useQueryInvalidation();
  const [loadError, setLoadError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<AgenteIntegradorFormData>({
    resolver: zodResolver(agenteIntegradorSchema),
  });

  const { data: agenteData, isLoading: loadingAgente, error: agenteError } = useQuery<AgenteIntegrador>({
    queryKey: ['agente-integrador', id],
    queryFn: () => agenteIntegradorService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (agenteError) {
      const message = handleError(agenteError as any, 'Erro ao carregar agente integrador');
      setLoadError(message);
    }
  }, [agenteError, handleError]);

  useEffect(() => {
    if (agenteData) {
      setLoadError(null);
      setValue('nome', agenteData.nome);
    }
  }, [agenteData, setValue]);

  const createAgenteMutation = useMutation({
    mutationFn: agenteIntegradorService.create,
    onSuccess: () => {
      invalidateAgentesIntegradores();
      handleSuccess('Agente integrador criado com sucesso!');
      navigate('/agentes-integradores');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao criar agente integrador');
    },
  });

  const updateAgenteMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgenteIntegradorFormData }) =>
      agenteIntegradorService.update(id, data),
    onSuccess: () => {
      invalidateAgentesIntegradores(id);
      handleSuccess('Agente integrador atualizado com sucesso!');
      navigate('/agentes-integradores');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao atualizar agente integrador');
    },
  });

  const onSubmit = async (data: AgenteIntegradorFormData) => {
    try {
      setIsSubmitting(true);
      if (isEditing) {
        await updateAgenteMutation.mutateAsync({ id: Number(id), data });
      } else {
        await createAgenteMutation.mutateAsync(data);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditing && loadError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar Agente Integrador</h1>
          <p className="mt-1 text-sm text-gray-500">Não foi possível carregar as informações do agente integrador.</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Erro ao carregar dados</CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/agentes-integradores')}>Voltar para lista</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isEditing && loadingAgente) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados do agente integrador...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Agente Integrador' : 'Novo Agente Integrador'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing
            ? 'Edite as informações do agente integrador'
            : 'Cadastre um novo agente integrador no sistema'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados do Agente Integrador</CardTitle>
            <CardDescription>
              Informações básicas do agente integrador
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome do Agente *</Label>
              <Input
                id="nome"
                placeholder="Ex: CIEE, Nube"
                {...register('nome')}
                className={errors.nome ? 'border-error-500' : ''}
              />
              {errors.nome && (
                <p className="mt-1 text-sm text-error-600">{errors.nome.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/agentes-integradores')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || loadingAgente}
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Agente' : 'Criar Agente')}
          </Button>
        </div>
      </form>
    </div>
  );
};
